/**
 * User config file + env resolution. Config lives at
 * `~/.config/atbash/config.json`. `resolve` reads a single field with
 * precedence: explicit flag → env var → config file → "".
 */
import {
  chmodSync,
  existsSync,
  mkdirSync,
  readFileSync,
  writeFileSync,
} from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";

export interface AtbashUserConfig {
  agentKey?: string;
  orgName?: string;
  judgeEndpoint?: string;
  blockchainRid?: string;
  provider?: string;
  providerModel?: string;
}

const ENV_MAP: Record<keyof AtbashUserConfig, string> = {
  agentKey: "ATBASH_AGENT_KEY",
  orgName: "ATBASH_ORG_NAME",
  judgeEndpoint: "ATBASH_ENDPOINT",
  blockchainRid: "ATBASH_BLOCKCHAIN_RID",
  provider: "ATBASH_PROVIDER",
  providerModel: "ATBASH_PROVIDER_MODEL",
};

export function getConfigDir(): string {
  const home = process.env.HOME || homedir() || "";
  return join(home, ".config", "atbash");
}

export function getConfigPath(): string {
  return join(getConfigDir(), "config.json");
}

export function loadUserConfig(): AtbashUserConfig {
  try {
    const p = getConfigPath();
    if (!existsSync(p)) return {};
    const raw = readFileSync(p, "utf-8").trim();
    if (!raw) return {};
    return JSON.parse(raw) as AtbashUserConfig;
  } catch (err) {
    console.error("Failed to load config file", err);
    return {};
  }
}

export function saveUserConfig(config: AtbashUserConfig): void {
  const dir = getConfigDir();
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true, mode: 0o700 });
  }
  const filePath = getConfigPath();
  writeFileSync(filePath, JSON.stringify(config, null, 2) + "\n", {
    mode: 0o600,
  });
  chmodSync(filePath, 0o600);
}

export function resolve(
  key: keyof AtbashUserConfig,
  flagValue?: string,
): string {
  if (flagValue) return flagValue;
  const envName = ENV_MAP[key];
  if (envName) {
    const envVal = process.env[envName];
    if (envVal) return envVal;
  }
  const fileVal = loadUserConfig()[key];
  if (fileVal != null) return String(fileVal);
  return "";
}
